import DashboardContentHeading from '../DashboardContentHeading/DashboardContentHeading';
import InspectionReqCard from './InspectionReqCard/InspectionReqCard';
import classes from './InspectionRequest.module.scss';
import { getUserInspections } from '../../../services'; // Import getUserInspections
import React, { useState, useEffect, useContext } from 'react';
import EmptyContentCard from '../../Layout/EmptyContentCard';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { setCarDetail } from '../../../store/reducers/carReducer';
import { useSnackbar } from 'notistack';
import AuthContext from '../../../store/auth-context';
import UpgradeModal from '../../UI/UpgradeModal';

const InspectionRequestSkeleton = () => {
    return (
        <div className={classes.skeletonCard}>
            <div className={classes.header}>
                <div className={classes.title}></div>
                <div className={classes.status}></div>
            </div>
            <div className={classes.details}>
                <div className={classes.detail}></div>
                <div className={classes.detail}></div>
                <div className={classes.detail}></div>
            </div>
        </div>
    );
};

const InspectionRequest = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [inspectionData, setInspectionData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const { isFreeTier } = useContext(AuthContext);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const getCarName=(content)=>
    {
      if (!content) return 'N/A';
      if (!content.makerDetail) return content.model || 'N/A';
      
      let vehicleName = (content.model || '') + " " + 
                        (content.makerDetail.maker || '') + " " + 
                        (content.makerDetail.model || '') + " " + 
                        (content.makerDetail.type || '');
      return vehicleName.trim() || 'N/A';
    }

    useEffect(() => {
        const fetchInspectionRequests = async () => {
            setIsLoading(true);
            try {
                const response = await getUserInspections();
                const { data: inspectionRequests } = response;
                if(inspectionRequests){
                    // Filter out inspections without valid car data and transform
                    const transformedData = inspectionRequests
                        .filter(content => content.carData) // Filter out null/undefined carData
                        .map((content, index) => ({
                            id: index + 1, // Generate a unique ID
                            name: getCarName(content.carData), // Use the provided getCarName function
                            auction: content.carData?.bidDetail?.bidSite || 'N/A',
                            bidNo: content.carData?.bidDetail?.bidNo || 'N/A',
                            grade: content.carData?.bidDetail?.bidNo?.[3] || 'N/A',
                            date: content.carData?.bidDetail?.bidDate 
                                ? new Date(content.carData.bidDetail.bidDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
                                : 'N/A', // Format date with fallback
                            status: content.status || 'Unknown', // Fallback if status is missing
                            carId: content.carData?._id, // Add the car ID for navigation
                        }));
                    setInspectionData(transformedData);
                }
            } catch (error) {
                console.error('Error fetching inspection requests:', error);
            } finally {
                setIsLoading(false);
            }
        };
    
        fetchInspectionRequests();
    }, []);
    
    const handleCardClick = (content) => {
        if (!content.carId) {
            console.error('Car ID is missing for navigation');
            enqueueSnackbar('Unable to view vehicle details', { 
                variant: 'error',
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                },
            });
            return;
        }
        
        dispatch(setCarDetail(null));
        navigate(`/detail/${content.carId}`, {
            state: { from: '/dashboard' }
        });
    };
 
    return (
        <div className={classes.InspectionRequest}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header section */}
                <div className={classes.InspectionRequestHeading}>
                    <DashboardContentHeading iconText='inspection' />
                </div>
                
                {/* Content section with VIP feature message for free tier users */}
                <div style={{ 
                    flex: '1 1 auto',
                    maxHeight: 'calc(100% - 60px)',
                    overflow: 'auto',
                    marginBottom: '10px',
                    fontSize: '0.95rem',
                    position: 'relative'
                }}>
                    {isFreeTier ? (
                        <div className={classes.vipFeatureCard}>
                            <div className={classes.lockIconContainer}>
                                <i className="fas fa-lock"></i>
                            </div>
                            <h3 className={classes.vipFeatureTitle}>VIP Feature</h3>
                            <p className={classes.vipFeatureDescription}>
                                Upgrade to VIP to access inspection requests and manage your vehicle inspections.
                            </p>
                            <button 
                                className={classes.upgradeButton}
                                onClick={onOpen}
                            >
                                Upgrade to VIP
                            </button>
                        </div>
                    ) : (
                        <>
                            {isLoading ? (
                                [...Array(3)].map((_, index) => (
                                    <InspectionRequestSkeleton key={index} />
                                ))
                            ) : inspectionData.length > 0 ? (
                                inspectionData.slice(0,3).map((content, index) => (
                                    <div key={index} className="inspection-card-text">
                                        <InspectionReqCard 
                                            content={content} 
                                            onClick={() => handleCardClick(content)}
                                        />
                                    </div>
                                ))
                            ) : (
                                <EmptyContentCard 
                                    header="You don't have any inspections yet." 
                                    message="Explore our assets and mark the ones you like the most, so you don't lose sight of them." 
                                />
                            )}
                        </>
                    )}
                </div>
                
                {/* Button section - only show for VIP users */}
                {!isFreeTier && (
                    <div style={{ 
                        marginTop: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        width: '100%',
                        padding: '0',
                        paddingLeft: '20px',
                        marginBottom: '20px',
                        height: '40px'
                    }}>
                        <button 
                            className='whiteAndPurp'
                            style={{
                                width: 'auto',
                                minWidth: '180px'
                            }} 
                            onClick={() => navigate('/garage?source=auction&tab=inspections')}
                        >
                            See all Inspections
                        </button>
                    </div>
                )}
            </div>
            <UpgradeModal isOpen={isOpen} onClose={onClose} />
        </div>
    );
};

export default InspectionRequest;